import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useInstructorAuth } from '../../context/InstructorAuthContext';
import api from '../../utils/api';
import './InstructorLogin.css';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const InstructorLogin = () => {
  const navigate = useNavigate();
  const { login } = useInstructorAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (error) setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/instructor-portal');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setForgotMsg('Please enter your email address.'); return; }
    setForgotLoading(true);
    setForgotMsg('');
    try {
      await api.post('/instructor-auth/forgot-password', { email: forgotEmail.trim() });
      setForgotMsg('✅ If an account exists with that email, a reset link has been sent. Check your inbox — and your spam or junk folder if you don\'t see it.');
    } catch (err) {
      setForgotMsg(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="il-page">
      {/* Brand bar */}
      <div className="il-brand">
        <span className="il-brand-dot" />
        <span className="il-brand-text">Fast Track <strong>Driving School</strong></span>
      </div>

      <div className="il-card">
        <div className="il-header">
          <h1 className="il-title">Instructor Portal</h1>
          <p className="il-subtitle">
            {showForgot ? 'Enter your email to receive a password reset link' : 'Sign in with the email and password you set during registration'}
          </p>
        </div>

        {!showForgot ? (
          <form className="il-form" onSubmit={handleLogin} noValidate>
            {error && <div className="il-alert il-alert--error">{error}</div>}

            <div className="il-field">
              <label className="il-label">Email Address</label>
              <input
                className="il-input"
                type="email"
                name="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                autoFocus
              />
            </div>

            <div className="il-field">
              <label className="il-label">Password</label>
              <div className="il-pw-wrap">
                <input
                  className="il-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="il-pw-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <button className="il-btn" type="submit" disabled={loading}>
              {loading ? <span className="il-spinner" /> : 'Sign In'}
            </button>

            <p className="il-hint">
              <button type="button" className="il-link" onClick={() => { setShowForgot(true); setError(''); }}>
                Forgot password?
              </button>
            </p>

            <p className="il-hint">
              Don't have an account?{' '}
              <Link className="il-link" to="/apply-signup">Apply to join →</Link>
            </p>
          </form>
        ) : (
          <form className="il-form" onSubmit={handleForgotPassword} noValidate>
            {forgotMsg && (
              <div className={`il-alert ${forgotMsg.startsWith('✅') ? 'il-alert--success' : 'il-alert--error'}`}>
                {forgotMsg}
              </div>
            )}
            <div className="il-field">
              <label className="il-label">Email Address</label>
              <input
                className="il-input"
                type="email"
                placeholder="your.email@example.com"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <button className="il-btn" type="submit" disabled={forgotLoading}>
              {forgotLoading ? <span className="il-spinner" /> : 'Send Reset Link'}
            </button>

            <p className="il-hint">
              <button type="button" className="il-link" onClick={() => { setShowForgot(false); setForgotMsg(''); }}>
                ← Back to Sign In
              </button>
            </p>
          </form>
        )}
      </div>

      <p className="il-footer-note">Fast Track Driving School UK · Instructor Portal</p>
    </div>
  );
};

export default InstructorLogin;

